import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';
import { getWhatsAppLink } from '../../utils/whatsapp';

// Excursion data with translations
const getExcursionsData = (language) => {
  const isEn = language === 'en';
  return [
    {
      id: 1,
      slug: 'orange-bay',
      name: 'Orange Bay',
      description: isEn ? 'Paradise island in the Red Sea' : 'Île paradisiaque en Mer Rouge',
      price: '25€',
      badge: isEn ? '⭐ Most popular' : '⭐ Le plus choisi',
      image: 'https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,w_1200,h_675,c_fill/v1767543316/orange-bay-hurghada-island_5_lf4q4c.jpg',
    },
    {
      id: 2,
      slug: 'dauphins-hurghada',
      name: isEn ? 'Swim with Dolphins' : 'Nager avec les Dauphins',
      description: isEn ? 'Magical encounter in the Red Sea' : 'Rencontre magique en Mer Rouge',
      price: '25€',
      image: 'https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,w_1200,h_675,c_fill/v1767546317/dolphin-house-snorkeling_1_ydusys.png',
    },
    {
      id: 3,
      slug: 'plongee-sous-marine',
      name: isEn ? 'Scuba Diving' : 'Plongée Sous-Marine',
      description: isEn ? 'Explore the depths of the Red Sea' : 'Explorez les fonds de la Mer Rouge',
      price: '30€',
      image: 'https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,w_1200,h_675,c_fill/v1767553362/scuba-diving-hurghada-underwater.jpg_3_eu72hs.png',
    },
    {
      id: 4,
      slug: 'safari-combine-quad-desert',
      name: 'Super Safari',
      description: isEn ? 'Quad, buggy and Bedouin village' : 'Quad, buggy et village bédouin',
      price: '35€',
      image: 'https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,w_1200,h_675,c_fill/v1767545994/safari-quad-jeep-desert-hurghada.png_9_wnaikb.png',
    },
    {
      id: 5,
      slug: 'le-caire-1-jour',
      name: isEn ? 'Cairo in 1 day' : 'Le Caire en 1 jour',
      description: isEn ? 'Pyramids, Sphinx and Museum' : 'Pyramides, Sphinx et Musée',
      price: '75€',
      image: 'https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,w_1200,h_675,c_fill/v1767546252/cairo-day-tour-pyramids_1_xgikpy.jpg',
    },
    {
      id: 6,
      slug: 'luxor-1-jour',
      name: isEn ? 'Luxor in 1 day' : 'Louxor en 1 jour',
      description: isEn ? 'Valley of the Kings and temples' : 'Vallée des Rois et temples',
      price: '75€',
      image: 'https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,w_1200,h_675,c_fill/v1767545544/luxor-day-tour-valley-of-the-kings_2_o6wses.png',
    },
    {
      id: 7,
      slug: 'balade-cheval-hurghada',
      name: isEn ? 'Horse Riding in Hurghada' : 'Balade à cheval à Hurghada',
      description: isEn ? 'An unforgettable experience between sea, desert and sunset.' : 'Une expérience inoubliable entre mer, désert et coucher de soleil.',
      price: '30€',
      image: 'https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,w_1200,h_675,c_fill/v1767544721/horse-riding-hurghada_1_whlt0w.png',
    },
    {
      id: 8,
      slug: 'paradise-island',
      name: 'Paradise Island',
      description: isEn ? 'Boat trip to a paradise island with snorkeling, lunch and relaxation.' : 'Excursion en bateau vers une île paradisiaque avec snorkeling, déjeuner et détente.',
      price: '35€',
      image: 'https://res.cloudinary.com/dzx5jx8fr/image/upload/f_auto,q_auto,w_1200/v1767608432/paradise-island-turquoise-water.png_3_lbc2ab.png',
    }
  ];
};

// WhatsApp SVG Icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const ExcursionsSection = () => {
  const { language, isEnglish } = useLanguage();
  const { t } = useTranslation();
  const excursions = getExcursionsData(language);
  const langPrefix = isEnglish ? '/en' : '';

  return (
    <section id="excursions" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-teal-50 text-teal-600 rounded-full text-sm font-medium mb-4 animate-in fade-in slide-in-from-bottom duration-500">
            {t('excursions.badge')}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 animate-in fade-in slide-in-from-bottom duration-500 delay-100">
            {t('excursions.title')}
          </h2>
          <p className="text-slate-500 text-lg max-w-2xl mx-auto animate-in fade-in slide-in-from-bottom duration-500 delay-200">
            {t('excursions.subtitle')}
          </p>
        </div>

        {/* Excursions Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {excursions.map((excursion, index) => (
            <div
              key={excursion.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl hover:border-teal-200 transition-all duration-300 hover:-translate-y-2 animate-in fade-in slide-in-from-bottom duration-500"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <Link to={`${langPrefix}/${excursion.slug}`}>
                <div className="relative h-48 sm:h-56 overflow-hidden">
                  <img
                    src={excursion.image}
                    alt={excursion.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                  {excursion.badge && (
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1.5 bg-amber-400 text-amber-900 rounded-full text-xs font-bold shadow-md">
                        {excursion.badge}
                      </span>
                    </div>
                  )}
                  <div className="absolute bottom-4 left-4">
                    <span className="px-3 py-1.5 bg-white/95 backdrop-blur-sm rounded-full text-sm font-semibold text-teal-600 shadow-sm">
                      {excursion.price}
                    </span>
                  </div>
                </div>
              </Link>

              {/* Content */}
              <div className="p-5">
                <Link to={`${langPrefix}/${excursion.slug}`}>
                  <h3 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-teal-600 transition-colors">
                    {excursion.name}
                  </h3>
                </Link>
                <p className="text-slate-500 mb-4">
                  {excursion.description}
                </p>
                <div className="flex gap-2">
                  <Button
                    asChild
                    variant="outline"
                    className="flex-1 rounded-full border-teal-200 text-teal-600 hover:bg-teal-50 transition-all duration-300"
                  >
                    <Link to={`${langPrefix}/${excursion.slug}`}>
                      {t('excursions.viewDetails')}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                  <Button
                    asChild
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    <a href={getWhatsAppLink(excursion.name, language)} target="_blank" rel="noopener noreferrer">
                      <WhatsAppIcon className="w-4 h-4 mr-1" />
                      WhatsApp
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ExcursionsSection;
